import CallMadeIcon from '@mui/icons-material/CallMadeRounded';
import { Typography, Box } from '@mui/material';
import CardMedia from '@mui/material/CardMedia';
import { off, onValue, query } from 'firebase/database';
import Lottie from 'lottie-react';
import {
  FC, useContext, useEffect, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import SpacingContent from 'components/SpacingContent';
import HowToPay from 'containers/KioskPaymentMethods/HowToPay';
import { PayButton } from 'containers/KioskPaymentMethods/HowToPay/index.styled';
import {
  ArrowsWrapper, ContentCard, PayTitle, Root,
} from 'containers/KioskPaymentMethods/index.styled';
import { PaymentContext, PaymentDispatchContext } from 'context/PaymentContext';
import { SystemDispatchContext } from 'context/SystemContext';

import { getHomeRoute } from 'hooks/useRouting';
import {
  bountyCommonLayerRef, orderPaymentResult, payOrder, updateOrderStatus,
} from 'services/Order';

import { rotationDegreesByTerminalPosition } from 'constants/checkout';
import { CHECKOUT_COMPLETE_ROUTE } from 'constants/clientRoutes';
import {
  LayoutType, OrderStatus, PaymentMethod, PaymentStatus, PaymentSteps,
} from 'constants/enums';
import { PAYMENT_METHOD } from 'constants/fields';
import { FAILED_PAYMENT_TIMESTAMP } from 'constants/general';
import { CartSummary } from 'types/cart.interface';
import { OrderDetails } from 'types/order.interface';
import { DejavooTransactionData } from 'types/payment.interface';
import { KioskSettings } from 'types/shopSettings.interface';
import { handleApiErrors } from 'utils/errorUtils';
import { formatRoute } from 'utils/formatters';
import { reportAlert } from 'utils/utils';

import paymentFailed from 'assets/animations/paymentFailed.json';
import paymentSuccessfulAnimation from 'assets/animations/paymentSuccessful.json';
import paymentUnavailable from 'assets/animations/paymentUnavailable.json';
import POS from 'assets/images/POS.png';

interface KioskPaymentMethodsProps {
  onPlaceOrder: (
    { ignoreDialog, shouldRedirect }: { ignoreDialog: boolean; shouldRedirect: boolean }
  ) => Promise<string>;
  kioskSettings?: KioskSettings | null;
  onChangeCheckout: (value: any, name: string) => void;
  isSubmitting?: boolean;
  cartPriceDetails: CartSummary;
}

const KioskPaymentMethods: FC<KioskPaymentMethodsProps> = ({
  onPlaceOrder,
  kioskSettings,
  onChangeCheckout,
  isSubmitting = false,
  cartPriceDetails,
}) => {
  const navigate = useNavigate();
  const { paymentDetails, paymentFlow } = useContext(PaymentContext);
  const { updatePaymentFlow, resetPaymentFlow } = useContext(PaymentDispatchContext);
  const { setLayoutType } = useContext(SystemDispatchContext);
  const { orderId, alleavesOrderId } = paymentDetails || {};
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);

  const handleClosePaymentMethodsScreen = () => updatePaymentFlow({ showKioskPaymentMethods: false });

  const handleApplicationLaunchFailed = () => {
    updatePaymentFlow({ paymentSteps: PaymentSteps.LaunchFailed, hasPaymentFailed: true });
  };

  const handleTransactionSuccessful = async (param: string) => {
    try {
      const dejavooTransactionData = JSON.parse(param) as DejavooTransactionData;
      await orderPaymentResult(orderId, { dejavooTransactionData });
      updatePaymentFlow({ paymentSteps: PaymentSteps.PaymentSuccessful });
    } catch (e) {
      handleApiErrors(e);
    }
  };

  const handleTransactionFailed = () => {
    updatePaymentFlow({ paymentSteps: PaymentSteps.PaymentFailed });
    setTimeout(() => {
      updatePaymentFlow({ paymentSteps: PaymentSteps.HowToPay, hasPaymentFailed: true });
    }, FAILED_PAYMENT_TIMESTAMP);
  };

  const handlePayOrder = async (orderId: string) => {
    try {
      const totalAmount = cartPriceDetails?.total?.money?.amount;

      if (window.AndroidPaymentTerminal?.pay && totalAmount) {
        window.onApplicationLaunchFailed = handleApplicationLaunchFailed;
        window.onTransactionSuccessful = handleTransactionSuccessful;
        window.onTransactionFailed = handleTransactionFailed;
        window.AndroidPaymentTerminal.pay(totalAmount, null, orderId);
        return;
      }

      if (!kioskSettings?.kioskId) {
        toast.error('Missing kiosk settings!');
        return;
      }

      await payOrder(orderId, kioskSettings?.kioskId);

      if (!paymentFlow.hasPaymentFailed) {
        subscribeToOrderDetails(orderId);
      }

      updatePaymentFlow({ paymentSteps: PaymentSteps.WaitForCard, hasPaymentFailed: false });
      setLayoutType(LayoutType.Default);
    } catch (error: any) {
      handleApiErrors(error);
    }
  };

  const handlePayWithCardHere = async () => {
    let id = order?.orderId || orderId;

    if (!paymentFlow.hasPaymentFailed) {
      onChangeCheckout(PaymentMethod.DEBIT, PAYMENT_METHOD.name);
      id = await onPlaceOrder({ ignoreDialog: true, shouldRedirect: false });

      if (!id) {
        handleClosePaymentMethodsScreen();
        toast.error('Something went wrong with the order, please try again.');
        return;
      }
    }

    await handlePayOrder(id);
  };

  const handleCancelOrder = async () => {
    if (!order || !order?.orderId) {
      toast.error('Missing information order id!');
      return;
    }

    try {
      setIsCanceling(true);
      await updateOrderStatus(order.orderId, OrderStatus.CANCELLED);
      toast.success('Your order has been cancelled!');
      navigate(getHomeRoute());
      resetPaymentFlow();
    } catch (e) {
      handleApiErrors(e);
    } finally {
      setIsCanceling(false);
    }
  };

  const subscribeToOrderDetails = (orderId: string) => {
    const dbQuery = query(bountyCommonLayerRef(orderId));

    onValue(dbQuery, (snapshot) => {
      const orderSnapshot = snapshot.val() as OrderDetails;

      if (orderSnapshot && orderSnapshot?.paymentInfo?.paymentStatus) {
        setOrder(orderSnapshot);
      }
    }, (error) => reportAlert(`order listener from terminal: ${JSON.stringify(error)}`));
  };

  const unsubscribeFromOrderReceipt = (orderId: string) => off(bountyCommonLayerRef(orderId));

  const checkPaymentStatus = async (placedOrder: OrderDetails) => {
    if (placedOrder?.paymentInfo?.paymentStatus === PaymentStatus.PAID) {
      unsubscribeFromOrderReceipt(placedOrder?.orderId);
      updatePaymentFlow({ paymentSteps: PaymentSteps.PaymentSuccessful });
    }

    if (placedOrder.paymentInfo?.paymentStatus === PaymentStatus.PAYMENT_FAILED) {
      const errorMessage = placedOrder.paymentInfo.failureMessage;
      toast.error(errorMessage || 'Something went wrong with the payment, please try again.');

      updatePaymentFlow({ paymentSteps: PaymentSteps.HowToPay, hasPaymentFailed: true });
      setLayoutType(LayoutType.Warning);
    }
  };

  useEffect(() => () => {
    setLayoutType(LayoutType.Default);
  }, []);

  useEffect(() => {
    if (order?.paymentInfo?.paymentStatus) {
      checkPaymentStatus(order);
    }
  }, [order?.paymentInfo?.paymentStatus]);

  const seeDetails = (time = 0) => {
    setTimeout(() => {
      resetPaymentFlow();
      navigate({
        pathname: formatRoute(CHECKOUT_COMPLETE_ROUTE, { orderId }),
        search: `?alleavesOrderId=${alleavesOrderId || ''}`,
      });
    }, time);
  };

  const handlePayAtVendor = async () => {
    if (!paymentFlow.hasPaymentFailed) {
      const id = await onPlaceOrder({ ignoreDialog: true, shouldRedirect: true });

      if (!id) {
        handleClosePaymentMethodsScreen();
      } else {
        resetPaymentFlow();
      }
    } else {
      seeDetails();
    }
  };

  return (
    <Root>
      {paymentFlow.paymentSteps === PaymentSteps.HowToPay && (
        <HowToPay
          onCancel={handleCancelOrder}
          isSubmitting={isSubmitting || isCanceling}
          onPayAtVendor={handlePayAtVendor}
          onPayWithCardHere={handlePayWithCardHere}
          hasPaymentFailed={paymentFlow.hasPaymentFailed}
        />
      )}

      {paymentFlow.paymentSteps === PaymentSteps.WaitForCard && kioskSettings?.terminalPosition && (
        <Box display="flex" flexDirection="column" flexGrow={1}>
          <PayTitle variant="h3">
            Press the
            {' '}
            <span>green button</span>
            {' '}
            in the top right of the terminal, then insert card.
          </PayTitle>

          <SpacingContent>
            <ContentCard>
              <CardMedia
                sx={{
                  objectFit: 'contain',
                  width: '100%',
                  height: '100%',
                }}
                component="img"
                src={POS}
              />
              <ArrowsWrapper rotationDegrees={rotationDegreesByTerminalPosition[kioskSettings?.terminalPosition]}>
                <CallMadeIcon />
              </ArrowsWrapper>
            </ContentCard>
          </SpacingContent>
        </Box>
      )}

      {paymentFlow.paymentSteps === PaymentSteps.LaunchFailed && (
        <SpacingContent>
          <ContentCard>
            <Lottie loop={false} animationData={paymentUnavailable} />
            <Typography variant="h2" textAlign="center" mb={5}>
              Payment
              {' '}
              <br />
              {' '}
              Unavailable
            </Typography>
          </ContentCard>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={10}>
            <Typography variant="h5" textAlign="center" fontWeight={400}>
              Please proceed to the counter to complete your payment.
            </Typography>
            <PayButton
              id="btn-pay-at-vendor"
              onClick={handlePayAtVendor}
              variant="contained"
              disabled={isSubmitting}
              sx={{ my: 5 }}
            >
              Pay at the counter
            </PayButton>
            <PayButton
              id="btn-cancel-order"
              onClick={handleCancelOrder}
              variant="outlined"
              disabled={isSubmitting}
            >
              Cancel Order
            </PayButton>
          </Box>
        </SpacingContent>
      )}

      {paymentFlow.paymentSteps === PaymentSteps.PaymentFailed && (
        <SpacingContent>
          <ContentCard>
            <Lottie loop={false} animationData={paymentFailed} />
            <Typography variant="h2" textAlign="center" mb={5}>
              Payment
              {' '}
              <br />
              {' '}
              Failed
            </Typography>
          </ContentCard>
          <Typography variant="h5" textAlign="center" mt={10}>
            Redirecting so you can try again...
          </Typography>
        </SpacingContent>
      )}

      {paymentFlow.paymentSteps === PaymentSteps.PaymentSuccessful && (
        <SpacingContent>
          <ContentCard>
            <Lottie loop={false} animationData={paymentSuccessfulAnimation} onComplete={() => seeDetails(100000)} />
            <Typography variant="h2" textAlign="center" mb={5}>
              Payment
              {' '}
              <br />
              {' '}
              Successful
            </Typography>
          </ContentCard>
          <Typography variant="h5" textAlign="center" mt={10}>
            Finalizing your order...
          </Typography>
        </SpacingContent>
      )}
    </Root>
  );
};

export default KioskPaymentMethods;
